import { computePosition, flip, shift, offset, size } from '@floating-ui/dom';
import { ReactRenderer } from '@tiptap/react';
import { Editor } from '@tiptap/core';
import { PluginKey } from '@tiptap/pm/state';
import { MentionOptions } from '@tiptap/extension-mention';
import { SuggestionProps, SuggestionKeyDownProps } from '@tiptap/suggestion';

import API from 'utils/api';
import { UserSearchResponse } from 'utils/api/modules/user/user.types';

import MentionList, { MentionListProps, MentionListRef } from './MentionList';

type MentionSuggestionProps = SuggestionProps<UserSearchResponse, { id: string; label: string }>;

const MENTION_SEARCH_LIMIT = 20;
const MENTION_SEARCH_DEBOUNCE_MS = 200;

export const COMMENT_MENTION_PLUGIN_KEY = new PluginKey('commentMention');

export const MENTION_DROPDOWN_ATTR = 'data-comment-mention-dropdown';

function getSelectionRect(editor: Editor): DOMRect {
  const { from } = editor.state.selection;
  const { left, right, top, bottom } = editor.view.coordsAtPos(from);
  return new DOMRect(left, top, right - left, bottom - top);
}

function positionDropdown(editor: Editor, element: HTMLElement, clientRect?: (() => DOMRect | null) | null): void {
  const rect = clientRect?.() || getSelectionRect(editor);
  const virtualElement = { getBoundingClientRect: () => rect };

  element.style.maxHeight = '';

  computePosition(virtualElement, element, {
    placement: 'bottom-start',
    strategy: 'fixed',
    middleware: [
      offset(4),
      flip({ padding: 8 }),
      shift({ padding: 8 }),
      size({
        padding: 8,
        apply({ availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            maxHeight: `${Math.max(120, availableHeight)}px`,
          });
        },
      }),
    ],
  }).then(({ x, y, strategy }) => {
    Object.assign(element.style, {
      position: strategy,
      left: `${x}px`,
      top: `${y}px`,
      zIndex: '1500',
    });
  });
}

export const commentMentionSuggestion: MentionOptions['suggestion'] = {
  pluginKey: COMMENT_MENTION_PLUGIN_KEY,
  items: (): UserSearchResponse[] => [],

  render: () => {
    let component: ReactRenderer<MentionListRef, MentionListProps> | null = null;

    let latestRequestId = 0;
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    const runSearch = async (props: MentionSuggestionProps): Promise<void> => {
      const requestId = (latestRequestId += 1);
      component?.updateProps({ ...props, items: component.props.items ?? [], loading: true });

      let users: UserSearchResponse[] = [];
      try {
        const { data } = await API.user.searchUsers({ query: props.query, limit: MENTION_SEARCH_LIMIT })();
        users = Array.isArray(data) ? data : [];
      } catch {
        users = [];
      }

      if (requestId !== latestRequestId || !component) {
        return;
      }
      component.updateProps({ ...props, items: users, loading: false });
      if (props.clientRect) {
        positionDropdown(props.editor, component.element as HTMLElement, props.clientRect);
      }
    };

    const search = (props: MentionSuggestionProps): void => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      component?.updateProps({ ...props, items: component.props.items ?? [], loading: true });
      debounceTimer = setTimeout(() => runSearch(props), MENTION_SEARCH_DEBOUNCE_MS);
    };

    const clearDebounce = (): void => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
        debounceTimer = null;
      }
    };

    return {
      onStart: (props: MentionSuggestionProps) => {
        component = new ReactRenderer(MentionList, {
          props: { ...props, items: [], loading: true },
          editor: props.editor,
        });

        if (!props.clientRect) {
          return;
        }

        component.element.style.position = 'fixed';
        component.element.setAttribute(MENTION_DROPDOWN_ATTR, '');
        document.body.appendChild(component.element);
        positionDropdown(props.editor, component.element as HTMLElement, props.clientRect);
        search(props);
      },

      onUpdate: (props: MentionSuggestionProps) => {
        if (!component) {
          return;
        }
        search(props);
        if (props.clientRect) {
          positionDropdown(props.editor, component.element as HTMLElement, props.clientRect);
        }
      },

      onKeyDown: (props: SuggestionKeyDownProps) => {
        if (props.event.key === 'Escape') {
          clearDebounce();
          component?.destroy();
          component?.element.remove();
          return true;
        }
        return component?.ref?.onKeyDown(props) ?? false;
      },

      onExit: () => {
        clearDebounce();
        component?.element.remove();
        component?.destroy();
        component = null;
      },
    };
  },
};
